import { useState } from "react";
import { arrayOf, shape, string } from "prop-types";
import HighlightText from "../../../../Common/js/components/HighlightText/HighlightText.jsx";
import * as R from "ramda";
import cx from "classnames";

const tagShape = shape({ value: string });

const propTypes = {
  nameLabel: string,
  externalRoleAndAffiliationLabel: string,
  topicsLabel: string,
  programRoleLabel: string,
  searchplaceholder: string,
  communityExperts: arrayOf(
    shape({
      title: string.isRequired,
      author: string.isRequired,
      url: string.isRequired,
      type: string.isRequired,
      tags: arrayOf(tagShape),
    }),
  ).isRequired,
};

const defaultProps = {
  nameLabel: "Name",
  externalRoleAndAffiliationLabel: "External Role & Affiliation",
  topicsLabel: "EGH Topic(s)",
  programRoleLabel: "EGH Program Role",
  searchplaceholder: "Search",
};

const Experts = (props) => {
  const {
    nameLabel,
    externalRoleAndAffiliationLabel,
    topicsLabel,
    programRoleLabel,
    searchplaceholder,
    communityExperts,
  } = props;

  const [query, setQuery] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);

  const filterOptions = [
    "name",
    "externalRoleAndAffiliation",
    "topics",
    "programRole",
  ];

  const handleSort = (field) => {
    sortField === field ? setSortAsc(!sortAsc) : null;
    setSortField(field);
  };

  const results =
    (communityExperts &&
      R.compose(
        R.sort(
          sortAsc ? R.ascend(R.prop(sortField)) : R.descend(R.prop(sortField)),
        ),
        R.filter(
          R.allPass([
            // make sure we're checking all fields
            R.anyPass(
              filterOptions.map((prop) =>
                R.propSatisfies(R.test(RegExp(query, "ig")), prop),
              ),
            ),
          ]),
        ),
      )(communityExperts)) ||
    [];

  return (
    <div className="experts">
      <div className="experts__filters">
        <input
          type="text"
          className="resource__search-text search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchplaceholder}
        />
      </div>
      <div className="experts__results">
        <table>
          <thead>
            <tr>
              <th className="experts__table-header">
                <button
                  className={cx("experts__sort", {
                    "experts__sort--ascending": sortAsc && sortField === "name",
                    "experts__sort--descending":
                      !sortAsc && sortField === "name",
                  })}
                  onClick={() => handleSort("name")}
                  type="button"
                >
                  {nameLabel}

                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-chevron-down"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </th>
              <th className="experts__table-header">
                <button
                  className={cx("experts__sort", {
                    "experts__sort--ascending":
                      sortAsc && sortField === "externalRoleAndAffiliation",
                    "experts__sort--descending":
                      !sortAsc && sortField === "externalRoleAndAffiliation",
                  })}
                  onClick={() => handleSort("externalRoleAndAffiliation")}
                  type="button"
                >
                  {externalRoleAndAffiliationLabel}

                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-chevron-down"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </th>
              <th className="experts__table-header">
                <button
                  className={cx("experts__sort", {
                    "experts__sort--ascending":
                      sortAsc && sortField === "topics",
                    "experts__sort--descending":
                      !sortAsc && sortField === "topics",
                  })}
                  onClick={() => handleSort("topics")}
                  type="button"
                >
                  {topicsLabel}

                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-chevron-down"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </th>
              <th className="experts__table-header">
                {" "}
                <button
                  className={cx("experts__sort", {
                    "experts__sort--ascending":
                      sortAsc && sortField === "programRole",
                    "experts__sort--descending":
                      !sortAsc && sortField === "programRole",
                  })}
                  onClick={() => handleSort("programRole")}
                  type="button"
                >
                  {programRoleLabel}

                  <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-chevron-down"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {results.map((result) => (
              <tr key={result.name}>
                <td>
                  <HighlightText query={query} text={result.name || ""} />
                </td>
                <td>
                  <HighlightText
                    query={query}
                    text={result.externalRoleAndAffiliation || ""}
                  />
                </td>
                <td>
                  <HighlightText query={query} text={result.topics || ""} />
                </td>
                <td>
                  <HighlightText
                    query={query}
                    text={result.programRole || ""}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

Experts.propTypes = propTypes;
Experts.defaultProps = defaultProps;
export default Experts;
